import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	TextField,
	Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useUploadFileMutation } from "../../Api/Mutations/useUploadFileMutation";
import { CODELIST_FILES_QUERY_KEY } from "../../Api/Queries/useCodeListFilesQuery";

type Props = {
	open: boolean;
	onClose: () => void;
	defaultFolderPath?: string | null;
};

const normalizeDefaultPath = (path?: string | null): string => {
	if (!path || path === "codelists") return "";
	if (path.startsWith("codelists/")) return path.replace("codelists/", "");
	return path;
};

const fileKey = (file: File): string =>
	`${file.name}-${file.size}-${file.lastModified}`;

export const FileUploadDialog: React.FC<Props> = ({
	open,
	onClose,
	defaultFolderPath,
}) => {
	const [files, setFiles] = useState<File[]>([]);
	const [uploadedCount, setUploadedCount] = useState(0);
	const [folderPath, setFolderPath] = useState(
		normalizeDefaultPath(defaultFolderPath),
	);

	useEffect(() => {
		if (open) {
			setFolderPath(normalizeDefaultPath(defaultFolderPath));
		}
	}, [open, defaultFolderPath]);

	const queryClient = useQueryClient();
	const showSnackbar = useSnackbar();
	const uploadMutation = useUploadFileMutation({ silent: true });

	const onDrop = useCallback((acceptedFiles: File[]) => {
		setFiles((prev) => {
			const existing = new Set(prev.map(fileKey));
			const additions = acceptedFiles.filter((f) => !existing.has(fileKey(f)));
			return [...prev, ...additions];
		});
	}, []);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		multiple: true,
		accept: {
			"text/csv": [".csv"],
			"application/vnd.ms-excel": [".xls"],
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
				".xlsx",
			],
			"text/xml": [".xml"],
			"application/xml": [".xml"],
			"application/json": [".json"],
		},
	});

	const removeFile = (key: string) => {
		setFiles((prev) => prev.filter((f) => fileKey(f) !== key));
	};

	const handleClose = () => {
		setFiles([]);
		setUploadedCount(0);
		setFolderPath(normalizeDefaultPath(defaultFolderPath));
		onClose();
	};

	const handleUpload = async () => {
		if (!isValid) return;

		const path = folderPath.startsWith("codelists/")
			? folderPath.replace("codelists/", "")
			: folderPath;

		const failedKeys = new Set<string>();
		const failedNames: string[] = [];
		let succeeded = 0;

		setUploadedCount(0);
		for (const file of files) {
			try {
				await uploadMutation.mutateAsync({ file, folderPath: path });
				succeeded += 1;
			} catch {
				failedKeys.add(fileKey(file));
				failedNames.push(file.name);
			} finally {
				setUploadedCount((c) => c + 1);
			}
		}

		if (succeeded > 0) {
			queryClient.invalidateQueries({ queryKey: [CODELIST_FILES_QUERY_KEY] });
		}

		if (failedKeys.size === 0) {
			showSnackbar(
				succeeded === 1
					? "Soubor byl úspěšně nahrán"
					: `Nahráno ${succeeded} souborů`,
				"success",
			);
			handleClose();
			return;
		}

		showSnackbar(
			`Nahráno ${succeeded} z ${
				files.length
			} souborů. Chyba u: ${failedNames.join(", ")}`,
			"error",
		);
		setFiles(files.filter((f) => failedKeys.has(fileKey(f))));
		setUploadedCount(0);
	};

	const isValid = files.length > 0 && folderPath.trim().length > 0;
	const isUploading = uploadMutation.isPending;

	const handleRequestClose = () => {
		if (!isUploading) handleClose();
	};

	return (
		<Dialog open={open} onClose={handleRequestClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Typography variant="h6">Nahrát soubor</Typography>
					<IconButton onClick={handleClose} size="small" disabled={isUploading}>
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent>
				<Box sx={{ mb: 3 }}>
					<TextField
						fullWidth
						label="Cílová složka"
						value={folderPath}
						onChange={(e) => setFolderPath(e.target.value)}
						placeholder="např. nsure/vehicles"
						helperText="Složka bude automaticky vytvořena pod codelists/"
						size="small"
						sx={{ mt: 1, mb: 3 }}
					/>

					<Box
						{...getRootProps()}
						sx={{
							border: 2,
							borderStyle: "dashed",
							borderColor: isDragActive ? "primary.main" : "divider",
							borderRadius: 2,
							p: 4,
							textAlign: "center",
							cursor: "pointer",
							backgroundColor: isDragActive
								? "action.hover"
								: "background.paper",
							transition: "all 0.2s ease",
							"&:hover": {
								borderColor: "primary.main",
								backgroundColor: "action.hover",
							},
						}}
					>
						<input {...getInputProps()} />
						<Box>
							<CloudUploadIcon
								sx={{ fontSize: 48, color: "action.active", mb: 1 }}
							/>
							<Typography variant="body1">
								{isDragActive
									? "Pusťte soubory zde..."
									: "Přetáhněte soubory nebo klikněte pro výběr"}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								Podporované formáty: CSV, XLS, XLSX, XML, JSON
							</Typography>
						</Box>
					</Box>

					{files.length > 0 && (
						<List dense sx={{ mt: 1 }}>
							{files.map((file) => (
								<ListItem
									key={fileKey(file)}
									secondaryAction={
										<IconButton
											edge="end"
											size="small"
											onClick={() => removeFile(fileKey(file))}
											disabled={isUploading}
										>
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									}
								>
									<ListItemIcon sx={{ minWidth: 36 }}>
										<InsertDriveFileIcon color="primary" fontSize="small" />
									</ListItemIcon>
									<ListItemText
										primary={file.name}
										secondary={`${(file.size / 1024).toFixed(1)} KB`}
									/>
								</ListItem>
							))}
						</List>
					)}
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button variant="text" onClick={handleClose} disabled={isUploading}>
					Zrušit
				</Button>
				<Button
					variant="text"
					onClick={handleUpload}
					disabled={!isValid || isUploading}
					startIcon={
						isUploading ? (
							<CircularProgress size={16} color="inherit" />
						) : undefined
					}
				>
					{isUploading
						? `Nahrávám ${uploadedCount}/${files.length}...`
						: files.length > 1
						  ? `Nahrát (${files.length})`
						  : "Nahrát"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
